// 前端呼叫後端 API 的薄包裝

import type {
  Meeting,
  MeetingReference,
  Job,
  CreateMeetingRequest,
  UpdateMeetingRequest,
  UpdateReferenceRequest,
  SetPlaudSourceRequest,
  SetManualSourceRequest,
  AudioUploadResponse,
  AddReferenceRequest,
  ProcessRequest,
  ProcessResponse,
  RefineRequest,
  MeetingMessage,
  ApiError,
  RefineResponse,
  ReplaceRule,
  ReplaceScope,
  ReplaceResponse,
  ShareLink,
  CreateShareRequest,
  CreateShareResponse,
  MeetingAttachment,
} from "@/types";

class ApiClient {
  private base = "/api";

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const res = await fetch(`${this.base}${path}`, {
      method,
      headers: body ? { "Content-Type": "application/json" } : {},
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
    if (!res.ok) {
      const err = (await res
        .json()
        .catch(() => ({ error: `HTTP ${res.status}` }))) as ApiError;
      throw new Error(err.error || "Unknown error");
    }
    return res.json() as Promise<T>;
  }

  // === Meetings ===
  listMeetings() {
    return this.request<{ meetings: Meeting[] }>("GET", "/meetings");
  }

  getMeeting(id: string) {
    return this.request<{ meeting: Meeting }>("GET", `/meetings/${id}`);
  }

  createMeeting(data: CreateMeetingRequest) {
    return this.request<{ meeting: Meeting }>("POST", "/meetings", data);
  }

  updateMeeting(id: string, data: UpdateMeetingRequest) {
    return this.request<{ ok: true }>("PATCH", `/meetings/${id}`, data);
  }

  deleteMeeting(id: string) {
    return this.request<{ ok: true }>("DELETE", `/meetings/${id}`);
  }

  // === Sources ===
  setPlaudSource(id: string, data: SetPlaudSourceRequest) {
    return this.request<{ ok: true }>(
      "POST",
      `/meetings/${id}/source/plaud`,
      data,
    );
  }

  setManualSource(id: string, data: SetManualSourceRequest) {
    return this.request<{ ok: true }>(
      "POST",
      `/meetings/${id}/source/manual`,
      data,
    );
  }

  getAudioUploadUrl(id: string, fileName: string, fileSize: number) {
    return this.request<AudioUploadResponse>(
      "POST",
      `/meetings/${id}/source/audio`,
      { file_name: fileName, file_size: fileSize },
    );
  }

  // === References ===
  listReferences(id: string) {
    return this.request<{ references: MeetingReference[] }>(
      "GET",
      `/meetings/${id}/references`,
    );
  }

  addReference(id: string, data: AddReferenceRequest) {
    return this.request<{ reference_id: number }>(
      "POST",
      `/meetings/${id}/references`,
      data,
    );
  }

  updateReference(id: string, refId: number, data: UpdateReferenceRequest) {
    return this.request<{ ok: true }>(
      "PATCH",
      `/meetings/${id}/references/${refId}`,
      data,
    );
  }

  deleteReference(id: string, refId: number) {
    return this.request<{ ok: true }>(
      "DELETE",
      `/meetings/${id}/references/${refId}`,
    );
  }

  // === Process ===
  startProcessing(id: string, data: ProcessRequest = {}) {
    return this.request<ProcessResponse>(
      "POST",
      `/meetings/${id}/process`,
      data,
    );
  }

  getJob(jobId: string) {
    return this.request<{ job: Job }>("GET", `/jobs/${jobId}`);
  }

  /**
   * Claude Code 模式：把會議標記成「等 Claude Code 回寫」。
   * 不觸發任何 AI 呼叫，只換狀態，讓列表看得出這場在等終端機。
   */
  markAwaitingCli(id: string) {
    return this.request<{ ok: true }>("PATCH", `/meetings/${id}`, {
      status: "awaiting_cli",
    });
  }

  /** 交給麥閒助理：Mac 上的 App 用 Max 額度整理，好了 LINE 通知（2026-10-19） */
  sendToAssistant(id: string) {
    return this.request<{ ok: true; app_online: boolean }>("POST", `/meetings/${id}/assistant`, {});
  }

  getActiveJob(meetingId: string) {
    return this.request<{ job: Job | null }>(
      "GET",
      `/meetings/${meetingId}/active-job`,
    );
  }

  // === Refine（對話迭代）===
  refine(id: string, data: RefineRequest) {
    return this.request<RefineResponse>(
      "POST",
      `/meetings/${id}/refine`,
      data,
    );
  }

  listMessages(id: string) {
    return this.request<{ messages: MeetingMessage[] }>(
      "GET",
      `/meetings/${id}/messages`,
    );
  }

  clearMessages(id: string) {
    return this.request<{ ok: true }>(
      "DELETE",
      `/meetings/${id}/messages`,
    );
  }

  // === Transcript（逐字稿編輯）===
  getTranscript(id: string) {
    return this.request<{ transcript: string }>(
      "GET",
      `/meetings/${id}/transcript`,
    );
  }

  updateTranscript(id: string, transcript: string) {
    return this.request<{ ok: true; length: number }>(
      "PATCH",
      `/meetings/${id}/transcript`,
      { transcript },
    );
  }

  // === 批次字串替換 ===
  batchReplace(id: string, rules: ReplaceRule[], scope: ReplaceScope = "both") {
    return this.request<ReplaceResponse>(
      "POST",
      `/meetings/${id}/replace`,
      { rules, scope },
    );
  }

  // === 附帶頁面 ===
  setAttachments(id: string, attachments: MeetingAttachment[]) {
    return this.request<{ attachments: MeetingAttachment[] }>(
      "PUT",
      `/meetings/${id}/attachments`,
      { attachments },
    );
  }

  // === 分享連結 ===
  listShares(id: string) {
    return this.request<{ shares: ShareLink[] }>(
      "GET",
      `/meetings/${id}/share`,
    );
  }

  createShare(id: string, data: CreateShareRequest) {
    return this.request<CreateShareResponse>(
      "POST",
      `/meetings/${id}/share`,
      data,
    );
  }

  deleteShare(id: string, code: string) {
    return this.request<{ ok: true; deleted: number }>(
      "DELETE",
      `/meetings/${id}/share/${code}`,
    );
  }
}

export const api = new ApiClient();
